/**
 * NewEntityButton — the global "+ New" front door.
 *
 * Lives in the TopBar so any user can start a new project from any
 * page. New-project creation routes to the
 * /request wizard — the lightweight 5-step intake any business user
 * can complete. PMO triages from Intake → Pending Approval.
 *
 * The old 25-field admin form (EditProjectDialog) is still used for
 * EDITING existing projects (reached via the Edit affordances on the
 * project detail page + Portfolio rows), but new-project creation
 * intentionally doesn't go through it anymore — having business users
 * face that form was the original design problem.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronDown, FolderPlus } from "lucide-react";

export function NewEntityButton() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close the menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setMenuOpen((v) => !v)}
        className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 transition-colors"
      >
        <Plus className="h-3.5 w-3.5" />
        New
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>

      {menuOpen && (
        <div className="absolute right-0 z-50 mt-1 w-56 overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          <button
            onClick={() => { setMenuOpen(false); navigate("/request"); }}
            className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15"
          >
            <FolderPlus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-navy-500" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-ink">Request a Project</div>
              <div className="mt-0.5 text-[10px] leading-snug text-ink-muted">
                Quick 5-step form. PMO reviews next.
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
